'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { partnershipService } from '@/lib/services/partnershipService';

interface PartnershipFormData {
  organizationName: string;
  organizationType: string;
  contactName: string;
  email: string;
  phone: string;
  website: string;
  partnershipType: string;
  description: string;
  goals: string;
}

interface PartnershipInquiryFormProps {
  onSubmit: (data: PartnershipFormData) => void;
  onCancel: () => void;
}

const PartnershipInquiryForm: React.FC<PartnershipInquiryFormProps> = ({
  onSubmit,
  onCancel,
}) => {
  const [formData, setFormData] = useState<PartnershipFormData>({
    organizationName: '',
    organizationType: '',
    contactName: '',
    email: '',
    phone: '',
    website: '',
    partnershipType: '',
    description: '',
    goals: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const result = await partnershipService.submitInquiry(formData);
      
      if (result) {
        alert('Partnership Inquiry Submitted Successfully!\n\nThank you for your interest in partnering with SIG Day. Our team will review your inquiry and contact you within 5-7 business days.\n\nYou will receive a confirmation email shortly.');
        onSubmit(formData);
      } else {
        setSubmitError('Failed to submit inquiry. Please try again.');
      }
    } catch (error: any) {
      setSubmitError('An error occurred. Please try again later.');
      console.error('Partnership inquiry submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-card rounded-lg p-8 border border-border max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-headline font-bold text-foreground">
          Partnership Inquiry
        </h2>
        <button
          onClick={onCancel}
          className="p-2 hover:bg-muted rounded-full transition-colors"
          aria-label="Close form"
        >
          <Icon name="XMarkIcon" size={24} className="text-muted-foreground" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Organization Name *
            </label>
            <input
              type="text"
              required
              value={formData.organizationName}
              onChange={(e) =>
                setFormData({ ...formData, organizationName: e.target.value })
              }
              className="w-full px-4 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
              placeholder="Your Organization"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Organization Type *
            </label>
            <select
              required
              value={formData.organizationType}
              onChange={(e) =>
                setFormData({ ...formData, organizationType: e.target.value })
              }
              className="w-full px-4 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
            >
              <option value="">Select type</option>
              <option value="ngo">NGO/Non-Profit</option>
              <option value="government">Government Agency</option>
              <option value="faith">Faith-Based Organization</option>
              <option value="educational">Educational Institution</option>
              <option value="corporate">Corporate/Business</option>
              <option value="media">Media Organization</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Contact Name *
            </label>
            <input
              type="text"
              required
              value={formData.contactName}
              onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
              className="w-full px-4 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
              placeholder="John Doe"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Email *</label>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-4 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
              placeholder="contact@organization.org"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Phone *</label>
            <input
              type="tel"
              required
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full px-4 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
              placeholder="+1 (555) 123-4567"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Website (Optional)
            </label>
            <input
              type="url"
              value={formData.website}
              onChange={(e) => setFormData({ ...formData, website: e.target.value })}
              className="w-full px-4 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
              placeholder="https://www.organization.org"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Partnership Type *
          </label>
          <select
            required
            value={formData.partnershipType}
            onChange={(e) => setFormData({ ...formData, partnershipType: e.target.value })}
            className="w-full px-4 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
          >
            <option value="">Select partnership type</option>
            <option value="event">Event Co-Hosting</option>
            <option value="advocacy">Advocacy Collaboration</option>
            <option value="resource">Resource Sharing</option>
            <option value="funding">Funding Partnership</option>
            <option value="research">Research Collaboration</option>
            <option value="media">Media Partnership</option>
            <option value="strategic">Strategic Alliance</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Organization Description *
          </label>
          <textarea
            required
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            rows={4}
            className="w-full px-4 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground resize-none"
            placeholder="Tell us about your organization's mission, values, and current initiatives..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Partnership Goals *
          </label>
          <textarea
            required
            value={formData.goals}
            onChange={(e) => setFormData({ ...formData, goals: e.target.value })}
            rows={4}
            className="w-full px-4 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground resize-none"
            placeholder="What do you hope to achieve through partnership with SIG Day (The Global No Enemy Festival)? What value can you bring to the movement?"
          />
        </div>

        {submitError && (
          <div className="bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded-md text-sm">
            {submitError}
          </div>
        )}

        <div className="flex items-center justify-end space-x-4 pt-4">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="px-6 py-3 bg-muted text-foreground rounded-md font-medium transition-colors hover:bg-muted/80 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-6 py-3 bg-accent text-accent-foreground rounded-md font-cta font-bold transition-all duration-300 hover:bg-accent/90 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PartnershipInquiryForm;